import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from '../components/LegalLayout';
import { COMPANY } from '../lib/company';
import { useSEO } from '../hooks/useSEO';

const { emails } = COMPANY;

const sections = [
  {
    id: 'purpose',
    title: 'Purpose',
    body: (
      <p>
        This Acceptable Use Policy (&ldquo;AUP&rdquo;) sets the rules for using the IP9 proxy network operated by {COMPANY.legalName}. It forms part of our <Link to="/terms-of-service">Terms of Service</Link> and applies to every
        customer, sub-user and anyone using your credentials. Our network relies on real residential and mobile connections, so lawful and respectful use protects the people and partners who make it possible.
      </p>
    ),
  },
  {
    id: 'permitted',
    title: 'Permitted use',
    body: (
      <p>
        You may use the Services for lawful business and research purposes, for example: collecting publicly available data, market research, price and availability monitoring, ad verification, brand protection, SEO and SERP
        monitoring, localisation and quality-assurance testing, and academic research.
      </p>
    ),
  },
  {
    id: 'prohibited',
    title: 'Prohibited use',
    body: (
      <>
        <p>You must not use the Services, or allow them to be used, to:</p>
        <ul>
          <li>break any law or regulation, or facilitate anyone else doing so;</li>
          <li>access, store or distribute child sexual abuse material, or content that promotes terrorism or violent extremism;</li>
          <li>commit fraud, including payment fraud, identity theft, phishing, impersonation, account takeover or credential stuffing;</li>
          <li>gain unauthorised access to any system, account or data, or carry out vulnerability scanning or penetration testing without the owner&rsquo;s written permission;</li>
          <li>launch or take part in denial-of-service attacks, or send traffic that overloads or disrupts any network or website;</li>
          <li>distribute malware or operate botnets, command-and-control servers or crypto-mining on third-party resources;</li>
          <li>send spam or unsolicited bulk messages, or create fake accounts at scale;</li>
          <li>commit ad fraud, click fraud, or fake engagement such as fake views, likes, reviews or followers;</li>
          <li>collect or process personal data without a lawful basis, or in breach of the UK GDPR, the EU GDPR or other privacy laws, including scraping special-category data or building profiles of individuals without a lawful basis;</li>
          <li>infringe intellectual property rights, including downloading or distributing pirated content;</li>
          <li>harass, stalk, threaten or dox any person;</li>
          <li>evade sanctions, export controls or the lawful restrictions of financial, government or law enforcement systems;</li>
          <li>access targets we list as restricted in our <Link to="/docs/residential-proxies/restricted-targets">documentation</Link>; or</li>
          <li>resell, sublicense or share access to the Services without our written agreement, or try to identify, contact or interfere with the devices and people that provide network connectivity.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'good-practice',
    title: 'Responsible automation',
    body: (
      <ul>
        <li>Apply reasonable request rates and back-off so you do not degrade the websites you access.</li>
        <li>Respect technical access controls. Do not bypass authentication, paywalls or CAPTCHAs in a way that breaks the law or a binding agreement.</li>
        <li>Review and comply with the terms of the websites you access, where they bind you.</li>
        <li>Keep proxy credentials secret and rotate them if they may have been exposed.</li>
      </ul>
    ),
  },
  {
    id: 'enforcement',
    title: 'Monitoring and enforcement',
    body: (
      <>
        <p>
          We do not inspect the content of your traffic. We do monitor connection metadata, such as volumes, target hostnames and error patterns, and we review abuse reports. If we reasonably believe you have breached this AUP,
          we may:
        </p>
        <ul>
          <li>ask you for information about your use;</li>
          <li>block particular targets, throttle traffic, or suspend sub-users or credentials;</li>
          <li>suspend or terminate your account, as set out in the Terms of Service; and</li>
          <li>report suspected criminal activity to law enforcement and cooperate with lawful requests from competent authorities.</li>
        </ul>
        <p>Where appropriate and lawful, we will tell you what we did and why, and you can contest it by contacting {emails.support}.</p>
      </>
    ),
  },
  {
    id: 'report',
    title: 'Reporting abuse',
    body: (
      <p>
        If you believe traffic from our network is abusing your systems or breaking the law, email <a href={`mailto:${emails.abuse}`}>{emails.abuse}</a> with the source IP addresses, timestamps (with time zone), target URLs or
        hostnames, and a description. We investigate every report and aim to respond within 2 business days. Authorities can reach our point of contact at <a href={`mailto:${emails.legal}`}>{emails.legal}</a>.
      </p>
    ),
  },
];

const AcceptableUsePolicyPage = () => {
  useSEO('acceptableUse');
  return <LegalLayout title="Acceptable Use Policy" sections={sections} />;
};

export default AcceptableUsePolicyPage;
